import {
  fetchSeasonCampaignDetail,
  fetchSeasonCampaigns,
  fetchActiveGlobalSeason,
} from '../championship/api.js';
import { renderChampionshipPanel } from '../championship/renderChampionshipPanel.js';
import { mountStandingsApp, type StandingsController } from '../standings/StandingsApp.js';
import { createPageShell } from '../ui/pageShell.js';
import { createPageEmptyState } from '../ui/pageEmptyState.js';
import { createPillButton } from '../ui/pillButton.js';
import { wrapInPillButtonGroup } from '../ui/pillButtonGroup.js';
import { replaceAppHash } from '../ui/appHash.js';
import {
  bindStandingsDriverTooltips,
  removeStandingsDriverTooltip,
} from '../standings/standingsDriverTooltip.js';
import {
  bindStandingsPointsBreakdownTooltips,
  removeStandingsPointsBreakdownTooltip,
} from '../standings/standingsPointsBreakdownTooltip.js';
import { parseCampaignKeyFromHash, seasonsCampaignHash, seasonsTabHash } from './seasonsHash.js';
import { openSeasonHistoryModal } from './openSeasonHistoryModal.js';
import { renderHistoricSeasonPanel } from './renderHistoricSeasonPanel.js';
import { mountCampaignStandingsEmbed } from './mountCampaignStandingsEmbed.js';
import {
  bindDivisionCardStandingsHover,
  highlightDivisionCard,
} from './bindDivisionCardStandingsHover.js';

export type { SeasonsTabId } from './seasonsHash.js';
export { parseSeasonsTabFromHash } from './seasonsHash.js';

let seasonsHost: HTMLElement | null = null;
let seasonsController: SeasonsController | null = null;
let seasonsPageInflight: Promise<void> | null = null;

type SeasonsController = {
  syncFromHash: () => void;
  refreshSaisonHistory: () => Promise<void>;
  refreshActive: () => Promise<void>;
};

async function wireSeasonsView(
  body: HTMLElement,
  standingsHost: HTMLElement,
  championshipHost: HTMLElement,
  standingsSection: HTMLElement
): Promise<void> {
  let standingsController: StandingsController | null = null;
  let standingsMode: 'active' | 'campaign' | null = null;
  let viewingCampaignKey: string | null = null;
  let unbindDivisionHover: (() => void) | null = null;
  let campaignsCache: Awaited<ReturnType<typeof fetchSeasonCampaigns>> = [];

  const clearDivisionHover = (): void => {
    unbindDivisionHover?.();
    unbindDivisionHover = null;
  };

  const wireDivisionHover = (): void => {
    clearDivisionHover();
    if (!standingsController) return;
    unbindDivisionHover = bindDivisionCardStandingsHover(championshipHost, {
      selectDivision: (id) => {
        standingsController?.selectDivision?.(id);
        highlightDivisionCard(championshipHost, id);
      },
      getSelectedDivision: () => standingsController?.getSelectedDivision?.() ?? 1,
    });
  };

  const openHistoryModal = async (): Promise<void> => {
    try {
      campaignsCache = await fetchSeasonCampaigns();
    } catch {
      campaignsCache = [];
    }
    const picked = await openSeasonHistoryModal(body, campaignsCache);
    if (!picked) return;
    replaceAppHash(seasonsCampaignHash(picked.campaignKey));
    await showCampaign(picked.campaignKey);
  };

  const appendHistoryButton = (head: HTMLElement): void => {
    const historyBtn = createPillButton('Historique', {
      variant: 'soft',
      small: true,
      class: 'championship-history-btn',
      onClick: () => {
        void openHistoryModal();
      },
    });
    const cancelBtn = head.querySelector('.championship-cancel-btn');
    if (cancelBtn instanceof HTMLElement) {
      cancelBtn.remove();
      head.appendChild(
        wrapInPillButtonGroup([historyBtn, cancelBtn], {
          small: true,
          class: 'championship-head-actions',
          ariaLabel: 'Actions saison',
        })
      );
      return;
    }
    head.appendChild(historyBtn);
  };

  const showActiveSeason = async (): Promise<void> => {
    viewingCampaignKey = null;
    clearDivisionHover();
    removeStandingsDriverTooltip();
    removeStandingsPointsBreakdownTooltip();
    const globalView = await fetchActiveGlobalSeason().catch(() => null);
    await renderChampionshipPanel(championshipHost, {
      hostClassName: 'seasons-championship-host',
      panelId: 'seasons-championship-heading',
      preloadedGlobalView: globalView,
      skipInitialLoader: true,
      appendHeaderActions: (head) => appendHistoryButton(head),
    });
    // Classement seulement une fois la saison lancée (active/terminée) :
    // pas d’écran vide au démarrage (création / inscriptions).
    const showStandings =
      globalView != null && (globalView.status === 'active' || globalView.status === 'completed');
    if (!showStandings) {
      standingsSection.hidden = true;
      standingsHost.innerHTML = '';
      standingsController = null;
      standingsMode = null;
      clearDivisionHover();
      return;
    }
    standingsSection.hidden = false;
    if (standingsMode !== 'active' || !standingsController) {
      standingsController = mountStandingsApp(standingsHost, {
        embedded: true,
        compact: true,
      });
      standingsMode = 'active';
    } else {
      standingsController.refresh();
    }
    wireDivisionHover();
  };

  const showCampaign = async (campaignKey: string): Promise<void> => {
    viewingCampaignKey = campaignKey;
    clearDivisionHover();
    removeStandingsDriverTooltip();
    removeStandingsPointsBreakdownTooltip();
    championshipHost.innerHTML = '<p class="seasons-loading">Chargement de la saison…</p>';
    standingsHost.innerHTML = '';
    try {
      const detail = await fetchSeasonCampaignDetail(campaignKey);
      renderHistoricSeasonPanel(championshipHost, detail, {
        onBackToActive: () => {
          replaceAppHash(seasonsTabHash('saison'));
          void showActiveSeason();
        },
      });
      standingsController = mountCampaignStandingsEmbed(standingsHost, detail);
      standingsMode = 'campaign';
      standingsSection.hidden = false;
      wireDivisionHover();
    } catch (err) {
      standingsSection.hidden = true;
      championshipHost.innerHTML = '';
      championshipHost.appendChild(
        createPageEmptyState({
          title: 'Campagne introuvable',
          description: err instanceof Error ? err.message : 'Erreur de chargement',
          primaryAction: { label: 'Retour à la saison', href: '#saisons' },
        })
      );
      championshipHost.querySelectorAll<HTMLAnchorElement>('a[href^="#"]').forEach((a) => {
        a.addEventListener('click', (e) => {
          e.preventDefault();
          replaceAppHash('saisons');
          void showActiveSeason();
        });
      });
      standingsController = null;
      standingsMode = null;
    }
  };

  const syncFromHash = (): void => {
    const campaignKey = parseCampaignKeyFromHash();
    if (window.location.hash.includes('division=')) {
      replaceAppHash(campaignKey ? seasonsCampaignHash(campaignKey) : 'saisons');
    }
    if (campaignKey) {
      if (campaignKey !== viewingCampaignKey) {
        void showCampaign(campaignKey);
      }
      return;
    }
    if (viewingCampaignKey || !standingsController) {
      void showActiveSeason();
    }
  };

  const refreshActive = async (): Promise<void> => {
    if (viewingCampaignKey) {
      await showCampaign(viewingCampaignKey);
      return;
    }
    await showActiveSeason();
  };

  // Charger avant d’exposer le controller : sinon un refresh concurrent
  // (mount + refreshSeasonsApp) lance un 2ᵉ fetch pendant le premier.
  const campaignKey = parseCampaignKeyFromHash();
  if (window.location.hash.includes('division=')) {
    replaceAppHash(campaignKey ? seasonsCampaignHash(campaignKey) : 'saisons');
  }
  if (campaignKey) {
    await showCampaign(campaignKey);
  } else {
    await showActiveSeason();
  }

  seasonsController = {
    syncFromHash,
    refreshSaisonHistory: async () => {
      try {
        campaignsCache = await fetchSeasonCampaigns();
      } catch {
        campaignsCache = [];
      }
    },
    refreshActive,
  };

  if (body.dataset.championshipListener !== '1') {
    body.dataset.championshipListener = '1';
    window.addEventListener('multirx-championship-updated', (): void => {
      void seasonsController?.refreshActive();
    });
  }
}

async function renderSeasonsPage(root: HTMLElement): Promise<void> {
  seasonsController = null;
  root.innerHTML = '';
  root.className = 'app-view circuits-app seasons-app seasons-app--compact';
  root.dataset.tabsReady = '1';

  const { page, body } = createPageShell({
    title: 'Saisons',
    wide: true,
  });
  page.classList.add('seasons-page--compact');

  const activeSection = document.createElement('section');
  activeSection.className = 'seasons-section seasons-section--active';
  const activeHost = document.createElement('div');
  activeHost.id = 'seasons-championship-host';
  activeHost.className = 'seasons-championship-host';
  bindStandingsDriverTooltips(activeHost);
  bindStandingsPointsBreakdownTooltips(activeHost);
  activeSection.appendChild(activeHost);

  const standingsSection = document.createElement('section');
  standingsSection.className = 'seasons-section seasons-section--standings';
  standingsSection.hidden = true;
  standingsSection.innerHTML = `
    <div class="seasons-section-head">
      <h2>Classement</h2>
    </div>`;
  const standingsHost = document.createElement('div');
  standingsHost.className = 'seasons-standings-host';
  standingsSection.appendChild(standingsHost);

  body.append(activeSection, standingsSection);
  root.appendChild(page);

  // Rendu championnat + visibilité classement via showActiveSeason.
  await wireSeasonsView(body, standingsHost, activeHost, standingsSection);
}

function ensureSeasonsPage(): Promise<void> {
  if (!seasonsHost) return Promise.resolve();
  if (seasonsController) {
    seasonsController.syncFromHash();
    return seasonsController.refreshActive();
  }
  if (!seasonsPageInflight) {
    seasonsPageInflight = renderSeasonsPage(seasonsHost).finally(() => {
      seasonsPageInflight = null;
    });
  }
  return seasonsPageInflight;
}

export function mountSeasonsApp(root: HTMLElement): void {
  if (seasonsHost !== root) {
    seasonsController = null;
    seasonsPageInflight = null;
  }
  seasonsHost = root;
  void ensureSeasonsPage();
}

export function refreshSeasonsApp(): void {
  void ensureSeasonsPage();
}
